import { ApiProvider } from '@/provider/ApiProvider';

/**
 * Server write keys (authorization part 7c).
 *
 * Owner decision 2026-10-07: view is view, edit is edit, create is create. A write control is
 * shown only when the active role holds ONE of the keys the server enforces for that write
 * (the server checks "any of" the list). The lists mirror the server exactly:
 * - mservices/contract `contract.can:contract_permissions,<keys>` (routes/api.php, contract-common.php)
 * - api-contract-node `@ContractAuthz({ permissions })` (strategic-fit, impact, right-package,
 *   right-management controllers)
 * - mservices/activation `activation.can:<unit>,ActivationAccess::CAN_*`
 * See pnet-docs/authz-review/part7c-keys-contract.md and part7c-keys-activation.md.
 */
export const WRITE_KEYS = {
  /** Contract/AddBonus, AddTemplate (convert to template), AddContractTemplate (add from template). */
  bonusAdd: ['bonus_create', 'bonus_manage'],
  /** PUT Contract/Bonus/{id} (edit a contract bonus or its comment). */
  bonusEdit: ['bonus_edit', 'bonus_manage'],
  /** Contract/ContractAchieveBonus (record as achieved). */
  bonusAchieve: ['bonus_manage', 'bonus_edit'],
  /** Contract/ContractAddCustomBonus. */
  bonusAddCustom: ['add_custom_bonus', 'bonus_manage'],
  /** Contract/DeleteContractBonus, DELETE Contract/DeleteAchieveBonus/{id}. */
  bonusDelete: ['bonus_delete', 'bonus_manage'],

  /** PUT Contract/Edit (the contract view General tab Edit / Update). */
  contractEdit: ['contract_edit', 'contract_light'],

  /** Contract/Investment/addContractInvestment. */
  investmentCreate: ['contract_investment_create'],
  /** PUT Contract/EditFinancial/{id}. */
  investmentEdit: ['contract_investment_edit'],
  /** DELETE Contract/Investment/DeleteElementInvestment/{id}. */
  investmentDelete: ['contract_investment_delete'],

  /** DocumentMedia/AddDocumentMedia (Strategic Relevance attachments). */
  documentCreate: ['documents_create'],
  /** DELETE DocumentMedia/DeleteDocumentMedia/{id}. */
  documentDelete: ['documents_delete'],

  /**
   * strategic-relevance/strategic-fit: ensure, reset-template, save-step-weights, submit (self and
   * assessment); impact: recommendations PATCH, custom-recommendations, strategic-sign-reasons.
   */
  strategicFitEdit: ['strategic_fit_edit'],
  /** strategic-relevance/impact discussion-comments POST / PATCH, recommendations/refresh. */
  impactComment: ['contract_impact_view', 'strategic_fit_edit'],
  /** Legacy Strategic Relevance: POST / PUT Contract/StrategicData/{id} (submit, calibrate). */
  strategicDataEdit: ['strategic_fit_edit', 'calibrate_assessment'],

  /** right-package update, update-assets, delete-assets. */
  packageEdit: ['edit_package'],
  /** right-package delete. */
  packageDelete: ['delete_package'],
  /** right-package duplicate. */
  packageDuplicate: ['duplicate_package'],
  /** right-contract use-package / use-right-asset: send a package or assets to a contract. */
  packageSendToContract: ['contract_use_package', 'send_to_contract'],

  /** right-management property-brand-inventory/{brandId}/note, update-asset-review. */
  inventoryEdit: ['inventory_edit'],

  /** GET Activation/copyActivation/{id}: a copy creates an activity (owner decision 2026-10-07). */
  activationCopy: ['activation_create'],
  /** Activation/Campaign/Add (Comparison "Add New Activation", /addActivation). */
  campaignCreate: ['campaign_create', 'activation_create_product_launch'],
  /** PUT Activation/OKR/Update/{id} (Retrospective OKR table edit). Edit is edit: the add key does not edit. */
  okrUpdate: ['brand_okr_update', 'okr_objective_update'],
  /** dbs AddAudienceMapping, UpdateAudienceMapping/{id}, DeleteAudienceMapping/{id} (/audienceinterest). */
  audienceWrite: ['excel_uploads'],
  /** DELETE Activation/Campaign/DeleteActivation: unlink an activity from a campaign. */
  campaignUnlink: ['campaign_update'],

  /**
   * Gateway unit gateway_theme (mservices/api): POST ColorPalette, POST ChangeTheme, POST Font,
   * POST Font/Upload, DELETE Font. Reading the theme (GET ColorPalette, Font, UserFont) has no key.
   */
  themeEdit: ['edit_theme'],
} as const;

export type WriteAction = keyof typeof WRITE_KEYS;

type HasKey = typeof ApiProvider.permission;

const roleHasKey: HasKey = (key) => Boolean(ApiProvider.permission(key));

/** True when the active role holds at least one of the keys. */
export function hasAnyKey(keys: readonly string[], has: HasKey = roleHasKey): boolean {
  return keys.some((key) => has(key));
}

/** True when the server would accept this write from the active role. */
export function canWrite(action: WriteAction, has: HasKey = roleHasKey): boolean {
  return hasAnyKey(WRITE_KEYS[action], has);
}
